import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';
import BEM from '../../../../../../common/bem';
import TCClient from '../../../../../../client';
import { OidcFormInputField } from './OidcFormInputField';
import { validateEmailDomains, validateUrl } from './utils';
import { useOidcTestConnection } from './useOidcTestConnection';
import Button from 'admin/shared-components/Button';
import { Toast } from 'admin/shared-components/Toast/Toast';
import { Switch } from 'common/components/WebComponents';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import type { OidcEndpointsValidationResponse } from 'js-sdk/src/services/Admin/utils/types';

const classes = BEM.with('AuthForm');

type EndpointField = keyof Omit<OidcEndpointsValidationResponse, 'isValid' | 'errors' | 'warnings'>;

const ENDPOINT_MAP: Array<{
  keyword: string;
  field: EndpointField;
  formField: keyof OidcFormFillableData;
  label: string;
}> = [
  {
    keyword: 'AuthorizationEndpoint',
    field: 'authorizationEndpoint',
    formField: 'oidcAuthorizationEndpoint',
    label: 'Authorization Endpoint',
  },
  {
    keyword: 'TokenEndpoint',
    field: 'tokenEndpoint',
    formField: 'oidcTokenEndpoint',
    label: 'Token Endpoint',
  },
  {
    keyword: 'UserInfoEndpoint',
    field: 'userInfoEndpoint',
    formField: 'oidcUserInfoEndpoint',
    label: 'User Info Endpoint',
  },
  {
    keyword: 'JwksEndpoint',
    field: 'jwksEndpoint',
    formField: 'oidcJwksEndpoint',
    label: 'JWKS Endpoint',
  },
];

export type OidcFormBaseData = {
  oidcClientId: string;
  oidcClientSecret: string;
  oidcIssuerURL: string;
  oidcEmailDomains?: string;
};

type IsManualConfig = { oidcIsManualConfig: boolean };

export type OidcFormFillableData = {
  oidcAuthorizationEndpoint?: string;
  oidcTokenEndpoint?: string;
  oidcUserInfoEndpoint?: string;
  oidcJwksEndpoint?: string;
};

export type OidcFormData = OidcFormBaseData & OidcFormFillableData & IsManualConfig;

export const OidcComponent: React.FC<{
  setFormIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setFormIsValid }) => {
  const organization = useAppSelector((state) => state.admin.selectedOrganization);
  const oidcClientId = useAppSelector((state) => state.orgSettings.settings.oidcClientId ?? '');
  const oidcClientSecret = useAppSelector(
    (state) => state.orgSettings.settings.oidcClientSecret ?? ''
  );
  const oidcIssuerURL = useAppSelector((state) => state.orgSettings.settings.oidcIssuerURL ?? '');
  const oidcEmailDomains = useAppSelector(
    (state) => state.orgSettings.settings.oidcEmailDomains ?? ''
  );
  const oidcIsManualConfig = useAppSelector(
    (state) => state.orgSettings.settings.oidcIsManualConfig ?? false
  );
  const oidcAuthorizationEndpoint = useAppSelector(
    (state) => state.orgSettings.settings.oidcAuthorizationEndpoint ?? ''
  );
  const oidcTokenEndpoint = useAppSelector(
    (state) => state.orgSettings.settings.oidcTokenEndpoint ?? ''
  );
  const oidcUserInfoEndpoint = useAppSelector(
    (state) => state.orgSettings.settings.oidcUserInfoEndpoint ?? ''
  );
  const oidcJwksEndpoint = useAppSelector(
    (state) => state.orgSettings.settings.oidcJwksEndpoint ?? ''
  );
  const dispatch = useAppDispatch();

  const [warnings, setWarnings] = useState<Array<string>>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidatingEndpoints, setIsValidatingEndpoints] = useState(false);
  const [endpointValidation, setEndpointValidation] =
    useState<OidcEndpointsValidationResponse | null>(null);
  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    type: 'SUCCESS' | 'FAILURE';
  }>({
    isOpen: false,
    message: '',
    type: 'SUCCESS',
  });
  const {
    control,
    watch,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm<OidcFormData>({
    defaultValues: {
      oidcClientId,
      oidcClientSecret,
      oidcIssuerURL,
      oidcEmailDomains,
      oidcIsManualConfig,
      oidcAuthorizationEndpoint,
      oidcTokenEndpoint,
      oidcUserInfoEndpoint,
      oidcJwksEndpoint,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    return () => {
      setFormIsValid(true);
    };
  }, [setFormIsValid]);
  const isManualConfig = watch('oidcIsManualConfig');
  const fillableInputsAreDisabled = !isManualConfig;
  const issuerUrl = watch('oidcIssuerURL');
  const authorizationEndpoint = watch('oidcAuthorizationEndpoint');
  const tokenEndpoint = watch('oidcTokenEndpoint');
  const userInfoEndpoint = watch('oidcUserInfoEndpoint');
  const jwksEndpoint = watch('oidcJwksEndpoint');

  const [manualUrisHaveErrors, setManualUrisHaveErrors] = useState<boolean>(isManualConfig);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    // turning on → needs validation; turning off → no errors
    setManualUrisHaveErrors(isManualConfig);
  }, [isManualConfig]);
  const formIsValid = isValid && (isManualConfig ? !manualUrisHaveErrors : true);
  useEffect(() => {
    setFormIsValid(formIsValid);
  }, [formIsValid, setFormIsValid]);

  const issuerValidationIdRef = useRef(0);
  const endpointValidationIdRef = useRef(0);
  const clientId = watch('oidcClientId');
  const clientSecret = watch('oidcClientSecret');
  const emailDomains = watch('oidcEmailDomains');
  const { testOidcConnection, isInitiatingTest, isPopupOpen } = useOidcTestConnection(
    organization?.token,
    setToast,
    {
      issuerUrl,
      clientId,
      clientSecret,
      authorizationEndpoint,
      tokenEndpoint,
      userInfoEndpoint,
      jwksEndpoint,
      isManualConfig,
      emailDomains,
    }
  );

  const handleChange = useCallback(
    (fieldName: keyof OidcFormData, value: string) => {
      dispatch(actions.updateSettings({ [fieldName]: value }));
    },
    [dispatch]
  );

  const clearEndpointFields = useCallback(() => {
    const emptyFields = {
      oidcAuthorizationEndpoint: '',
      oidcTokenEndpoint: '',
      oidcUserInfoEndpoint: '',
      oidcJwksEndpoint: '',
    };
    (Object.keys(emptyFields) as Array<keyof typeof emptyFields>).forEach((field) => {
      setValue(field, '');
    });
    dispatch(actions.updateSettings(emptyFields));
    setEndpointValidation(null);
  }, [setValue, dispatch]);

  const validateIssuerUrl = useCallback(async () => {
    if (!organization?.token) {
      setToast({
        isOpen: true,
        message: 'Organization not loaded. Please refresh the page.',
        type: 'FAILURE',
      });
      return;
    }
    const orgToken = organization.token;
    const submittedIssuerUrl = issuerUrl;
    const validationId = ++issuerValidationIdRef.current;
    setIsValidating(true);
    setWarnings([]);
    clearEndpointFields();
    try {
      const res = await TCClient.adminOrganizations.validateIssuerUrl({
        organizationId: orgToken,
        issuerUrl: submittedIssuerUrl,
      });
      if (issuerValidationIdRef.current !== validationId) return;
      if (!res.errors?.length) {
        setManualUrisHaveErrors(false);
        const endpointUpdates: Partial<OidcFormFillableData> = {};
        if (res.authorizationEndpoint?.length) {
          setValue('oidcAuthorizationEndpoint', res.authorizationEndpoint);
          endpointUpdates.oidcAuthorizationEndpoint = res.authorizationEndpoint;
        }
        if (res.tokenEndpoint?.length) {
          setValue('oidcTokenEndpoint', res.tokenEndpoint);
          endpointUpdates.oidcTokenEndpoint = res.tokenEndpoint;
        }
        if (res.userInfoEndpoint?.length) {
          setValue('oidcUserInfoEndpoint', res.userInfoEndpoint);
          endpointUpdates.oidcUserInfoEndpoint = res.userInfoEndpoint;
        }
        if (res.jwksEndpoint?.length) {
          setValue('oidcJwksEndpoint', res.jwksEndpoint);
          endpointUpdates.oidcJwksEndpoint = res.jwksEndpoint;
        }
        if (Object.keys(endpointUpdates).length) {
          dispatch(actions.updateSettings(endpointUpdates));
        }
      } else {
        setManualUrisHaveErrors(true);
        setError('oidcIssuerURL', { message: res.errors[0], type: 'custom' });
      }
      if (res.warnings?.length) {
        setWarnings(res.warnings);
      }
    } catch (error) {
      if (issuerValidationIdRef.current !== validationId) return;
      setManualUrisHaveErrors(true);
      setToast({
        isOpen: true,
        message: 'There was an Error validating the OIDC data, try again later.',
        type: 'FAILURE',
      });
      Sentry.logger.error('Error validating Issuer URL', { error });
    } finally {
      if (issuerValidationIdRef.current === validationId) {
        setIsValidating(false);
      }
    }
  }, [issuerUrl, organization?.token, setValue, dispatch, setError, clearEndpointFields]);

  const validateEndpoints = useCallback(async () => {
    if (!organization?.token) {
      setToast({
        isOpen: true,
        message: 'Organization not loaded. Please refresh the page.',
        type: 'FAILURE',
      });
      return;
    }
    const orgToken = organization.token;
    const submittedEndpoints = {
      authorizationEndpoint,
      tokenEndpoint,
      userInfoEndpoint,
      jwksEndpoint,
    };
    const validationId = ++endpointValidationIdRef.current;
    setIsValidatingEndpoints(true);
    setEndpointValidation(null);
    try {
      const res = await TCClient.adminOrganizations.validateEndpoints({
        organizationId: orgToken,
        ...submittedEndpoints,
      });
      if (endpointValidationIdRef.current !== validationId) return;
      setEndpointValidation(res);
      setManualUrisHaveErrors(!res.isValid);
    } catch (error) {
      if (endpointValidationIdRef.current !== validationId) return;
      setToast({
        isOpen: true,
        message: 'There was an Error validating the endpoints, try again later.',
        type: 'FAILURE',
      });
      Sentry.logger.error('Error validating endpoints', { error });
    } finally {
      if (endpointValidationIdRef.current === validationId) {
        setIsValidatingEndpoints(false);
      }
    }
  }, [organization?.token, authorizationEndpoint, tokenEndpoint, userInfoEndpoint, jwksEndpoint]);

  const getEndpointMessages = useCallback(
    (entry: (typeof ENDPOINT_MAP)[number]) => {
      if (!endpointValidation) return { status: undefined, errors: [], warnings: [] };
      const result = endpointValidation[entry.field];
      const errors = endpointValidation.errors.filter((m: string) =>
        m.toLowerCase().includes(entry.keyword.toLowerCase())
      );
      const warnings = endpointValidation.warnings.filter((m: string) =>
        m.toLowerCase().includes(entry.keyword.toLowerCase())
      );
      return { status: result?.status, errors, warnings };
    },
    [endpointValidation]
  );

  return (
    <div className={classes()}>
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          open={toast.isOpen}
          onClose={() => {
            setToast({ isOpen: false, message: '', type: 'SUCCESS' });
          }}
          autoHideDuration={4000}
        />
      )}
      <form>
        <div className="form-field">
          <OidcFormInputField
            name="oidcClientId"
            label="Client ID"
            type="text"
            control={control}
            errors={errors}
            rules={{ required: 'Client ID is required.' }}
            onChange={(value) => handleChange('oidcClientId', value)}
          />
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcClientSecret"
            label="Client Secret"
            type="password"
            control={control}
            errors={errors}
            rules={{ required: 'Client Secret is required.' }}
            onChange={(value) => handleChange('oidcClientSecret', value)}
          />
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcIssuerURL"
            label="Issuer URL"
            type="text"
            control={control}
            errors={errors}
            rules={{
              required: 'Issuer URL is required.',
              validate: (value) => validateUrl(value),
            }}
            onChange={(value) => handleChange('oidcIssuerURL', value)}
          />
          {warnings.length
            ? warnings.map((warning) => (
                <span key={warning} className="warning">
                  {warning}
                </span>
              ))
            : undefined}
          <div>
            <Button
              variant="outlined"
              onClick={validateIssuerUrl}
              type="button"
              disabled={!issuerUrl || !!errors.oidcIssuerURL || isValidating}
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </Button>
          </div>
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcEmailDomains"
            label="Email Domains"
            type="text"
            control={control}
            errors={errors}
            rules={{ validate: (value) => validateEmailDomains(value) }}
            onChange={(value) => handleChange('oidcEmailDomains', value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="oidcIsManualConfig">
            Enable Manual endpoint configuration for non-standard providers
          </label>
          <Controller
            name="oidcIsManualConfig"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onSwitch={() => {
                  const next = !field.value;
                  field.onChange(next);
                  dispatch(actions.updateSettings({ oidcIsManualConfig: next }));
                  if (!next) {
                    setEndpointValidation(null);
                  }
                }}
              />
            )}
          />
        </div>

        {ENDPOINT_MAP.map((entry) => {
          const {
            status,
            errors: fieldErrors,
            warnings: fieldWarnings,
          } = getEndpointMessages(entry);
          return (
            <div key={entry.formField} className="form-field">
              <OidcFormInputField
                name={entry.formField}
                label={entry.label}
                type="text"
                disabled={fillableInputsAreDisabled}
                control={control}
                errors={errors}
                onChange={(value) => {
                  setManualUrisHaveErrors(true); // if URI change, need revalidation
                  handleChange(entry.formField, value);
                }}
                validationStatus={status}
                validationErrors={fieldErrors}
                validationWarnings={fieldWarnings}
              />
            </div>
          );
        })}

        <div className="buttons-container">
          {isManualConfig ? (
            <Button
              variant="outlined"
              onClick={validateEndpoints}
              type="button"
              disabled={!authorizationEndpoint || !tokenEndpoint || isValidatingEndpoints}
            >
              {isValidatingEndpoints ? 'Validating...' : 'Validate Endpoints'}
            </Button>
          ) : undefined}

          <Button
            variant="outlined"
            onClick={testOidcConnection}
            type="button"
            disabled={!formIsValid || isPopupOpen || isInitiatingTest}
          >
            {isInitiatingTest ? 'Connecting...' : 'Test Connection'}
          </Button>
        </div>
      </form>
    </div>
  );
};
